import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Link from 'next/link';
import { KitButton } from 'components/common/cms-kit';
import { useTheme } from '@mui/material/styles';
import { isUserSubscriber } from '../../services/subscriptionUtils';
import { useSession } from 'next-auth/react';

const Container = styled('div')(({ $imageLocation }) => ({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'flex-end',
  padding: '0 30px',
  position: 'relative',
  '@media screen and (min-width: 601px)': {
    justifyContent: $imageLocation === 'left' ? 'right' : 'left',
    flexDirection: $imageLocation === 'left' ? 'row-reverse' : 'row',
    minHeight: '475px',
  },
  '@media screen and (max-width: 600px)': {
    flexDirection: 'row-reverse',
    justifyContent: 'right',
    height: '400px',
  },
}));

const EmptyContainer = styled('div')(({ $imageLocation }) => ({
  flexGrow: 1,
  flexShrink: 0,
  flex: 1,
  verticalAlign: 'top',
  '@media screen and (min-width: 601px)': {
    padding: '60px 10px',
    flexDirection: $imageLocation === 'left' ? 'row-reverse' : 'row',
    maxWidth: $imageLocation === 'center' ? '20%' : '50%',
  },
  '@media screen and (max-width: 600px)': {
    display: 'none',
  },
}));

const FloatingButton = styled('div')(({ $imageLocation }) => ({
  verticalAlign: 'top',
  padding: '60px 10px',
  position: 'absolute',
  top: '65%',
  left: $imageLocation === 'left' ? '52%' : '35%',
  '@media screen and (max-width: 600px)': {
    display: 'none',
  },
}));

const MobileFloatingButton = styled('div')({
  verticalAlign: 'top',
  padding: '15px 10px',
  position: 'absolute',
  top: '60%',
  left: '55%',
  '@media screen and (min-width: 601px)': {
    display: 'none',
  },
});

const ImageContainer = styled('div')(({ $imageLocation }) => ({
  position: 'relative',
  '@media screen and (min-width: 601px)': {
    marginLeft: $imageLocation === 'left' ? '10px' : 'auto',
    marginRight: $imageLocation === 'left' ? 'auto' : '20px',
    margin: 0,
    maxWidth: '50%',
    height: '100%',
  },
  '@media screen and (max-width: 600px)': {
    maxWidth: '85%',
    height: 'auto',
  },
}));

const HeroImage = styled('img')({
  '@media screen and (max-width: 600px)': {
    marginLeft: '-48%',
  },
});

const TitleContainer = styled('div')(({ $imageLocation }) => ({
  margin: 0,
  position: 'absolute',
  '@media screen and (min-width: 601px)': {
    left: $imageLocation === 'left' ? '52.45%' : '21%',
    top: '45%',
    maxWidth: '50%',
  },
  '@media screen and (max-width: 600px)': {
    left: '54%',
    top: '25%',
    maxWidth: '110%',
  },
}));

const Title = styled('div')(({ $imageLocation }) => ({
  textTransform: 'uppercase',
  '@media screen and (min-width: 601px)': {
    fontWeight: 700,
    maxWidth: '410px',
    fontSize: '3.375rem',
    lineHeight: '2.688rem',
    textAlign: $imageLocation === 'left' ? 'left' : 'right',
  },
  '@media screen and (max-width: 600px)': {
    fontWeight: 600,
    textAlign: 'left',
    marginLeft: '-12px',
    fontSize: '2rem',
    lineHeight: '2.25rem',
  },
}));

const ActionImageWithButton = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  const { data: session } = useSession();
  const [buttonText, setButtonText] = useState(fields?.buttonText);

  useEffect(() => {
    if (session) {
      if (isUserSubscriber(session, 'wms') && fields.buttonTextForSubscriber) {
        setButtonText(fields.buttonTextForSubscriber);
      }
    }
  }, [session]);

  return (
    <React.Fragment>
      <Container $imageLocation={fields.imageLocation}>
        <EmptyContainer $imageLocation={fields.imageLocation}></EmptyContainer>
        <ImageContainer $imageLocation={fields.imageLocation}>
          {fields.heroImage && (
            <HeroImage
              src={fields.heroImage.url}
              alt={fields.heroImage.label}
            />
          )}
        </ImageContainer>
        {fields.showButton === 'true' && (
          <>
            <FloatingButton $imageLocation={fields.imageLocation}>
              <KitButton
                round
                size="lg"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                }}
                target={fields.buttonUrl.target}
                rel="noopener noreferrer"
                href={fields.buttonUrl.href}
                component="a"
              >
                {buttonText}
              </KitButton>
            </FloatingButton>
            <MobileFloatingButton>
              <Link href={fields.buttonUrl.href}>
                <KitButton
                  round
                  style={{
                    backgroundColor: theme.palette.primary.light,
                  }}
                  target={fields.buttonUrl.target}
                  rel="noopener noreferrer"
                >
                  {buttonText}
                </KitButton>
              </Link>
            </MobileFloatingButton>
          </>
        )}
        <TitleContainer $imageLocation={fields.imageLocation}>
          <Title $imageLocation={fields.imageLocation}>{fields.title}</Title>
        </TitleContainer>
      </Container>
    </React.Fragment>
  );
};

export default ActionImageWithButton;
